import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function BeginnersGuide() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-beginners';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: "AllChinaBuySpreadsheet Beginner's Guide: Start Shopping China Products Today",
    description: "Complete beginner-friendly guide to using AllChinaBuySpreadsheet for first-time China product buyers. Step-by-step walkthrough covering category selection, price comparison, verification badges, and shipping basics.",
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: "Beginner's Guide", item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Young%20person%20learning%20on%20laptop%20with%20notebook%20in%20bright%20minimal%20room%2C%20educational%20atmosphere%2C%20fresh%20morning%20light%2C%20beginner%20friendly%20concept%2C%20clean%20modern%20interior%2C%20motivational%20photography%2C%20simple%20organized%20desk&width=1800&height=840&seq=beginner-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet beginners guide"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Beginner's Guide</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet Beginner's Guide: Start Shopping China Products Today</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            If you have never bought products from China before, the process can seem intimidating. Different platforms,
            language barriers, shipping concerns — it is a lot to take in. This beginner-friendly guide walks you through
            exactly how to use <strong className="text-primary-600 font-semibold">AllChinaBuySpreadsheet</strong> to make
            your first purchase with confidence, even if you have zero experience with international shopping.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Why Spreadsheets Make It Easier for Beginners</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Traditional marketplaces throw thousands of products at you with no structure. The
            <strong className="text-primary-600 font-semibold"> all china buy spreadsheet</strong> approach is
            fundamentally different — instead of browsing, you review. Each product in our
            <strong className="text-primary-600 font-semibold"> china product spreadsheet list</strong> is already
            organized by category, price tier, and verification status. You do not need to know Chinese, understand
            supplier ratings, or figure out shipping logistics before you even start. Everything is laid out in
            simple, comparable rows.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Your First Purchase: A Step-by-Step Walkthrough</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Start by selecting a category that interests you — shoes, hoodies, and accessories are popular entry points
            for beginners because sizing is straightforward and quality expectations are well-established. Once you click
            through to the main marketplace, you will see the <strong className="text-primary-600 font-semibold">
            spreadsheet shopping guide China</strong> layout with products arranged by verification status. Products
            with green badges have passed all quality checks and are the safest bets for first-time buyers.
          </p>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            Pay attention to the shipping estimates in the spreadsheet. For your first order, choose products with
            shipping times under 15 days and tracking numbers included. This gives you visibility into your package
            journey and reduces anxiety about international delivery. As you gain confidence, you can explore products
            with longer shipping times that offer even better pricing.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Ready for Your First Purchase?</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Start Shopping <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-safety" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Safety Guide: Full Verification Process &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-shipping" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Shipping &amp; Logistics: What to Know &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}